import TimeSignature from './TimeSignature';

/**
 * Meter timing shared by every TimeKeeper (web, native and mock), the web
 * Scheduler's metronome and the shared playable scene. The iOS and Android
 * native schedulers mirror this arithmetic (NativeScheduler.swift /
 * NativeScheduler.java) - keep the three in step.
 *
 * The tempo follows the meter's beat grouping:
 *
 *   - Simple meters (numerator 1 to 4, e.g. 2/2, 2/4, 3/4, 4/4, 3/8; and any
 *     x/1, x/2 or x/4 meter that is not compound, e.g. 8/4 or 5/4): the beat is
 *     the bottom note. 4/4 at 60 = 60 quarters a minute; 2/2 at 60 = 60 halves.
 *   - Compound meters (numerator 6, 9, 12 ... over any playable denominator,
 *     e.g. 6/8, 9/8, 12/8, 6/4, 6/16): the beat is the dotted note worth three
 *     bottom notes, so a bar has numerator / 3 beats. 6/8 at 60 = 60 dotted
 *     quarters (180 eighths) a minute.
 *   - Irregular meters (a numerator of 5 or more that is not a multiple of 3,
 *     over 8, 16 or 32, e.g. 5/8, 7/8, 11/8): beats are groups of two and
 *     three bottom notes. The default puts the twos first and one three last
 *     when the numerator is odd (5 = 2+3, 7 = 2+2+3, 11 = 2+2+2+2+3; 8 =
 *     2+2+2+2).
 *
 * A meter's beat grouping lists how many bottom notes each beat of the bar
 * holds (6/8 = [3, 3], 7/8 = [2, 2, 3], 4/4 = [1, 1, 1, 1]). The tempo number
 * counts the shortest beat in the grouping; a longer beat lasts
 * proportionally longer (in 7/8 at 60 the three-note beat lasts 1.5 s). The
 * metronome and count-in click once per beat, at the start of each group.
 *
 * Overriding the default grouping: a TimeSignature can carry its own
 * `beatGrouping` (the content's, e.g. an exercise prompt's `beat_grouping`, or
 * a score's tempo mark counted in another unit - a slow 6/8 in six is
 * [1, 1, 1, 1, 1, 1]). A player setting overrides the content through
 * `withPlayerBeatGrouping`. Every transport reads the grouping through
 * `beatGroupingOf`, so whichever override the meter carries is the one that
 * plays.
 *
 * A playable meter has a whole-number numerator of at least 1, a denominator
 * of 1, 2, 4, 8, 16 or 32 (so every bar holds a whole number of 32nd-note
 * ticks, the transport's tick unit), and, when it carries one, a beat grouping
 * of whole numbers of at least 1 that add up to the numerator. Bar length is
 * otherwise free: 8/4 or 12/8 written as one long bar is an ordinary meter.
 */

const PLAYABLE_DENOMINATORS: readonly number[] = [1, 2, 4, 8, 16, 32];

/** The smallest denominator whose irregular numerators group in twos and threes. */
const SMALLEST_IRREGULAR_DENOMINATOR = 8;

const isWholeNumberOfAtLeastOne = (value: number): boolean => Number.isInteger(value) && value >= 1;

/**
 * Whether a beat grouping fits a numerator: a non-empty list of whole numbers
 * of at least 1 that add up to it.
 */
export const isBeatGroupingFor = (
  beatGrouping: readonly number[] | undefined,
  numerator: number,
): boolean =>
  Array.isArray(beatGrouping) &&
  beatGrouping.length > 0 &&
  beatGrouping.every(isWholeNumberOfAtLeastOne) &&
  beatGrouping.reduce((total, group) => total + group, 0) === numerator;

export const isPlayableTimeSignature = (
  timeSignature: TimeSignature | undefined,
): timeSignature is TimeSignature =>
  timeSignature !== undefined &&
  timeSignature !== null &&
  isWholeNumberOfAtLeastOne(timeSignature.numerator) &&
  PLAYABLE_DENOMINATORS.includes(timeSignature.denominator) &&
  (timeSignature.beatGrouping === undefined ||
    isBeatGroupingFor(timeSignature.beatGrouping, timeSignature.numerator));

const describeTimeSignature = (timeSignature: TimeSignature | undefined): string => {
  const grouping = timeSignature?.beatGrouping;
  const groupingText = grouping === undefined ? '' : ` grouped ${grouping.join('+')}`;
  return `${timeSignature?.numerator}/${timeSignature?.denominator}${groupingText}`;
};

export const assertPlayableTimeSignature = (timeSignature: TimeSignature | undefined): void => {
  if (!isPlayableTimeSignature(timeSignature)) {
    throw new Error(
      `Time signature ${describeTimeSignature(timeSignature)} is not playable: ` +
        `the numerator must be a whole number of at least 1, the denominator one of ` +
        `${PLAYABLE_DENOMINATORS.join(', ')}, and a beat grouping whole numbers that ` +
        `add up to the numerator`,
    );
  }
};

/**
 * The default beat grouping of a meter (see the module comment): compound
 * meters in threes, irregular meters in twos with one three last when the
 * numerator is odd, everything else one beat per bottom note.
 */
export const defaultBeatGrouping = (timeSignature: TimeSignature): number[] => {
  const { numerator, denominator } = timeSignature;
  if (numerator >= 6 && numerator % 3 === 0) {
    return Array(numerator / 3).fill(3);
  }
  if (numerator >= 5 && denominator >= SMALLEST_IRREGULAR_DENOMINATOR) {
    const isOdd = numerator % 2 === 1;
    const twos = Array((isOdd ? numerator - 3 : numerator) / 2).fill(2);
    return isOdd ? [...twos, 3] : twos;
  }
  return Array(numerator).fill(1);
};

/** The beat grouping a transport plays: the meter's own override, else the default. */
export const beatGroupingOf = (timeSignature: TimeSignature): number[] => {
  assertPlayableTimeSignature(timeSignature);
  return timeSignature.beatGrouping === undefined
    ? defaultBeatGrouping(timeSignature)
    : [...timeSignature.beatGrouping];
};

/**
 * The meter with a player's beat grouping in place of the content's. The
 * player setting wins over the content; a grouping that does not fit the meter
 * is refused (an Error), and the meter is never changed in place.
 */
export const withPlayerBeatGrouping = (
  timeSignature: TimeSignature,
  playerBeatGrouping: readonly number[],
): TimeSignature => {
  const regrouped = new TimeSignature(timeSignature.numerator, timeSignature.denominator, [
    ...playerBeatGrouping,
  ]);
  assertPlayableTimeSignature(regrouped);
  return regrouped;
};

/** How many bottom notes the tempo number counts: the shortest beat of the grouping. */
export const tempoUnitInBottomNotes = (timeSignature: TimeSignature): number =>
  Math.min(...beatGroupingOf(timeSignature));

/** How many beats (metronome clicks) a bar has. */
export const beatsPerBar = (timeSignature: TimeSignature): number =>
  beatGroupingOf(timeSignature).length;

/** Seconds per tempo beat: the unit the tempo number counts. */
export const secondsPerBeat = (bpm: number): number => 60.0 / bpm;

/** Seconds per bottom note (an eighth in 6/8 or 7/8, a quarter in 4/4). */
export const secondsPerBottomNote = (bpm: number, timeSignature: TimeSignature): number =>
  secondsPerBeat(bpm) / tempoUnitInBottomNotes(timeSignature);

/** Seconds per bar: `numerator` bottom notes. */
export const measureDurationSecs = (bpm: number, timeSignature: TimeSignature): number =>
  secondsPerBottomNote(bpm, timeSignature) * timeSignature.numerator;

/** Seconds per whole note: `denominator` bottom notes. */
export const secondsPerWholeNote = (bpm: number, timeSignature: TimeSignature): number =>
  secondsPerBottomNote(bpm, timeSignature) * timeSignature.denominator;

/** Each beat of a bar in whole-note units (the unit of every measure timestamp). */
export const beatLengthsInWholeNotes = (timeSignature: TimeSignature): number[] =>
  beatGroupingOf(timeSignature).map((group) => group / timeSignature.denominator);

/** Each beat of a bar in seconds at the tempo. */
export const beatLengthsInSecs = (bpm: number, timeSignature: TimeSignature): number[] => {
  const bottomNoteSecs = secondsPerBottomNote(bpm, timeSignature);
  return beatGroupingOf(timeSignature).map((group) => bottomNoteSecs * group);
};

/**
 * The beat grid of one bar, as the metronome reads it: where each beat starts,
 * in bottom notes from the bar line, and whether every beat is the same length
 * (then a beat is simply a bar / beatsPerBar long).
 */
export interface MeterBeatGrid {
  beatsPerBar: number;
  numerator: number;
  beatStartsInBottomNotes: number[];
  isUniform: boolean;
}

export const beatGridOf = (timeSignature: TimeSignature): MeterBeatGrid => {
  const grouping = beatGroupingOf(timeSignature);
  // Each beat starts where the beats before it end: [2, 2, 3] -> [0, 2, 4].
  const beatStartsInBottomNotes = grouping.map((_group, index) =>
    grouping.slice(0, index).reduce((total, group) => total + group, 0),
  );
  return {
    beatsPerBar: grouping.length,
    numerator: timeSignature.numerator,
    beatStartsInBottomNotes,
    isUniform: grouping.every((group) => group === grouping[0]),
  };
};

const barOfBeat = (beatIndex: number, grid: MeterBeatGrid): number =>
  Math.floor(beatIndex / grid.beatsPerBar);

const beatInBarOf = (beatIndex: number, grid: MeterBeatGrid): number =>
  beatIndex - barOfBeat(beatIndex, grid) * grid.beatsPerBar;

/**
 * Where a beat starts, in bars from beat 0 (beat 0 is a downbeat). A uniform
 * grid keeps the long-standing beatIndex / beatsPerBar.
 */
export const beatStartInBars = (beatIndex: number, grid: MeterBeatGrid): number =>
  grid.isUniform
    ? beatIndex / grid.beatsPerBar
    : barOfBeat(beatIndex, grid) +
      grid.beatStartsInBottomNotes[beatInBarOf(beatIndex, grid)] / grid.numerator;

/**
 * Where a beat starts, in seconds from beat 0, for a bar of `measureSecs`. A
 * uniform grid keeps the long-standing beatIndex * (measureSecs / beatsPerBar).
 */
export const beatStartSecs = (
  beatIndex: number,
  measureSecs: number,
  grid: MeterBeatGrid,
): number =>
  grid.isUniform
    ? beatIndex * (measureSecs / grid.beatsPerBar)
    : beatStartInBars(beatIndex, grid) * measureSecs;

/**
 * The index of the last beat that starts at or before `elapsedSecs` from
 * beat 0 (negative before beat 0). A uniform grid keeps the long-standing
 * floor(elapsedSecs / (measureSecs / beatsPerBar)).
 */
export const beatIndexAtOrBefore = (
  elapsedSecs: number,
  measureSecs: number,
  grid: MeterBeatGrid,
): number => {
  if (grid.isUniform) {
    return Math.floor(elapsedSecs / (measureSecs / grid.beatsPerBar));
  }
  const bar = Math.floor(elapsedSecs / measureSecs);
  const bottomNotesIntoBar = ((elapsedSecs - bar * measureSecs) / measureSecs) * grid.numerator;
  const beatsStartedInBar = grid.beatStartsInBottomNotes.filter(
    (start) => start <= bottomNotesIntoBar,
  ).length;
  return bar * grid.beatsPerBar + Math.max(beatsStartedInBar, 1) - 1;
};

/** Beat 1 of a bar (the accented click). */
export const isDownbeat = (beatIndex: number, grid: MeterBeatGrid): boolean =>
  beatInBarOf(beatIndex, grid) === 0;

/**
 * The note the tempo number counts, for the tempo label: a glyph and its name.
 * x/4 simple meters keep the quarter-note glyph they always showed.
 */
export interface TempoBeatUnit {
  glyph: string;
  name: string;
}

const NOTE_GLYPHS: Record<number, TempoBeatUnit> = {
  1: { glyph: '\u{1D15D}', name: 'whole' },
  2: { glyph: '\u{1D15E}', name: 'half' },
  4: { glyph: '♩', name: 'quarter' },
  8: { glyph: '♪', name: 'eighth' },
  16: { glyph: '\u{1D161}', name: 'sixteenth' },
  32: { glyph: '\u{1D162}', name: 'thirty-second' },
};

export const tempoBeatUnitOf = (timeSignature: TimeSignature): TempoBeatUnit => {
  const unit = tempoUnitInBottomNotes(timeSignature);
  const { denominator } = timeSignature;
  // A beat of `unit` bottom notes is a plain note when unit / denominator is
  // 1 / 2^k, and a dotted note when it is 3 / 2^k.
  const plainNote = NOTE_GLYPHS[denominator / unit];
  if (plainNote !== undefined) {
    return plainNote;
  }
  // A dotted note of value 1 / n lasts 3 / (2n) whole notes, so the beat
  // (unit / denominator whole notes) is a dotted 1 / n note for
  // n = 3 * denominator / (2 * unit): 6/8 -> dotted quarter, 6/4 -> dotted half.
  const dottedNote = NOTE_GLYPHS[(3 * denominator) / (2 * unit)];
  if (dottedNote !== undefined) {
    return { glyph: `${dottedNote.glyph}.`, name: `dotted ${dottedNote.name}` };
  }
  const bottomNote = NOTE_GLYPHS[denominator];
  return { glyph: `${bottomNote.glyph}×${unit}`, name: `${unit} ${bottomNote.name}s` };
};
